import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import PlayButton from '../basic/svgs/PlayButton';
import DescriptivePlayButton from './buttons/DescriptivePlayButton';

import theme from '../../styles/theme';

const { colors } = theme;

const Padding = styled.div`
	background-color: ${colors.grayscale.charcoal};
	padding: ${({ customPadding }) =>
		customPadding ? customPadding : '56.25% 0 0 0'};
	position: relative;
	.grow {
		transition-duration: 0.25s;
	}
	:hover {
		.grow {
			transform: scale(1.1);
		}
	}
`;
const Wrapper = styled.div`
	position: absolute;
	height: 100%;
	width: 100%;
	left: 0;
	top: 0;
	/* hide wistia's own playbutton graphic: */
	div[id*="big_play_button"] {
		display: none !important;
	}

	.descriptive-play-button {
		background-color: ${props =>
			props.playButtonColor === 'Brand Orange'
				? `rgba(${colors.brand.rgb}, .85)`
				: props.playButtonColor === 'Turquoise'
					? `rgba(${colors.secondary.rgb}, .85)`
					: props.playButtonColor === 'Yellow'
						? `rgba(${colors.tertiary.rgb}, .85)`
						: props.playButtonColor === 'Purple'
							? `rgba(${colors.quaternary.rgb}, .85)`
							: `rgba(${colors.brand.rgb}, .85)`};
		display: flex;
		padding: 30px;
		padding: 20px 30px;
		border-radius: 30px;
		width: fit-content;
		transition: background-color 0.75s ease;
		img {
			margin-right: 12px;
		}
	}
	.regular-play-button {
		svg {
			fill: ${colors.brandMain};
			transition: all 0.5s ease;
		}
	}

	:hover {
		.regular-play-button {
			svg {
				fill: ${colors.primary.dark};
			}
		}

		.descriptive-play-button {
			background-color: ${props =>
				props.playButtonColor === 'Brand Orange'
					? colors.brandMain
					: props.playButtonColor === 'Turquoise'
						? colors.secondary.main
						: props.playButtonColor === 'Yellow'
							? colors.tertiary.main
							: props.playButtonColor === 'Purple'
								? colors.quaternary.main
								: colors.brandMain}
	}
`;
const Player = styled.span`
	display: inline-block;
	height: 100%;
	width: 100%;
`;
const Play = styled.div`
	/*pointer-events: none makes it so any click of play button goes to underlying video: */
	pointer-events: none;

	:hover {
		cursor: pointer;
	}
	:active {
		background-size: 95px 95px;
	}
	/*center play button: */
	position: absolute;
	left: 50%;
	top: 50%;

	margin-top: ${props => (props.descriptivePlayButton ? '-45px' : '-30px')};
	margin-left: ${props => (props.descriptivePlayButton ? '-122px' : '-30px')};

	width: ${props => (props.descriptivePlayButton ? '244px' : '60px')};
	height: ${props => (props.descriptivePlayButton ? '85px' : '60px')};
`;

const Disable = styled.div`
	background-color: transparent;
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;

	:hover {
		cursor: pointer;
	}
`;

export default React.memo(
	React.forwardRef((props, ref) => {
		// React useEffect hook to creat the wistia video scripts necessary to embed the video on the page.
		// The elements are created and then appended to the body (as well as an optional thumbnail script if passed through props)
		// The return() of the useEffect hook removes the scripts from the body of the html once the page/video component is unloaded.
		const [video, setVideo] = useState();
		const [playButton, setPlayButton] = useState(props.noPlayButton);

		useEffect(() => {
			// Create the Wista Scripts
			const script1 = document.createElement('script');
			const script2 = document.createElement('script');

			script1.id = `wistia-embed-script-${props.videoId}`;
			script2.id = `wistia-embed-player-script`;

			script1.src = `https://fast.wistia.com/embed/medias/${props.videoId}.jsonp`;
			script2.src = 'https://fast.wistia.com/assets/external/E-v1.js';

			// Wistia Script Options
			if (props.ABTest) {
				//<script src=“https://fast.wistia.com/embed/medias/8cvzx087hj.jsonp?idType=ab-test” async></script>
				script1.src += `?idType=ab-test`;
				console.log('ab test enabled');
			}
			script1.async = true;
			script2.async = true;

			// append scripts, if multiple videos on one page, only need append script2 once
			document.body.appendChild(script1);
			if (
				document.querySelectorAll(
					`script[src="https://fast.wistia.com/assets/external/E-v1.js"]`
				).length === 0
			) {
				document.body.appendChild(script2);
			}

			// target video to add options
			window._wq = window._wq || [];

			window._wq.push({
				id: props.videoId,
				options: {
					// PLAYBACK OPTIONS
					popover: props.noLightbox ? false : true,
					autoPlay: props.autoPlay ? props.autoPlay : false,
					popoverShowOnLoad: props.autoPlay && true,
					popoverOverlayOpacity: 0.95,
					endVideoBehavior: props.endVideoBehavior
						? props.endVideoBehavior
						: props.noLightbox
							? 'reset'
							: '',
					playButton: false,
					muted: props.muted && true,

					// UI Options
					stillUrl: props.thumbnail,
					controlsVisibleOnLoad: props.showControls ? true : false,

					//AB Test
					seo: props.ABTest && true,
					videoFoam: props.ABTest && true,
				},
				onReady: myOnReady,
			});
			// must remove these children else becomes unwieldy:
			return () => {
				document
					.querySelectorAll('[id^=wistia-embed]')
					.forEach(e => e.parentNode.removeChild(e));
				// leave popovers if there are several types of videos on the page

				{
					!props.mixedVideos &&
						document
							.querySelectorAll('[id$="popover_container"]')
							.forEach(e => e.parentNode.removeChild(e));
				}
			};
		}, []);

		const myOnReady = video => {
			setVideo(video);
		};

		// Remove the custom play button when either the video or an external play link is clicked
		const handleClick = () => {
			if (props.noLightbox) {
				setPlayButton(true);
			}
		};

		useEffect(() => {
			if (props.playExternally) {
				setPlayButton(true);
			}
		}, [props.playExternally]);

		return (
			<Padding
				{...props}
				key="wistia-video"
				thumbnail={props.thumbnail}
				customPadding={props.customPadding}
				className={`${props.className ? props.className : ''} ${props.customPadding ? props.customPadding : ' wistia_responsive_padding'}`}
			>
				<Wrapper
					className="wistia_responsive_wrapper"
					onClick={() => handleClick()}
					playButtonColor={props.playButtonColor}
				>
					<Player
						ref={ref}
						className={`wistia_embed 
							wistia_async_${props.videoId} 
							${props.ABTest && 'idType=ab-test'}`}
					/>
					{!playButton && (
						<Play
							descriptivePlayButton={props.videoTitle}
							className={
								props.videoTitle
									? 'descriptive-play-button'
									: 'regular-play-button'
							}
						>
							{props.videoTitle ? (
								<DescriptivePlayButton
									videoTitle={props.videoTitle}
									videoLength={props.videoLength}
								/>
							) : (
								<PlayButton />
							)}
						</Play>
					)}
					{props.disabled ? (
						<Disable onClick={() => props.disabledOnClick()} />
					) : null}
				</Wrapper>
			</Padding>
		);
	})
);
